import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { globalQueryKeys } from '@opallabs/api'
import { content, type OpalApiResponseDocument } from '@opallabs/api/v3'
import {
  type ReadableAccessMetadata,
  type WritableAccessMetadata,
} from '@opallabs/concepts'

type UpdateContentAccessContext = {
  contentUuid: string
}

type UpdateContentAccessParams = {
  access: WritableAccessMetadata
}

export const useUpdateContentAccess = ({
  contentUuid,
}: UpdateContentAccessContext) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: globalQueryKeys.contentAccess(contentUuid),
    mutationFn: ({ access }: UpdateContentAccessParams) =>
      content.update({
        params: {
          path: {
            content_id: contentUuid,
          },
          query: {
            expose: {
              meta: {
                access: 'true',
              },
            },
          },
        },
        requestBody: {
          data: {
            id: contentUuid,
            type: 'content',
          },
          meta: {
            access,
          },
        },
      }),
    onMutate({ access }) {
      const { data: groupData } =
        queryClient.getQueryData<
          OpalApiResponseDocument<'ReadUserGroupsV3', 200>
        >(globalQueryKeys.groups(undefined, ['asset', 'members'])) ?? {}

      queryClient.setQueryData<OpalApiResponseDocument<'ReadContentV3', 200>>(
        globalQueryKeys.contentAccess(contentUuid),
        (cachedData) =>
          produce(cachedData, (draftDocument) => {
            if (draftDocument?.meta?.access) {
              draftDocument.meta.access.users = access.users ?? []

              draftDocument.meta.access.groups =
                access.groups?.reduce<ReadableAccessMetadata['groups']>(
                  (groups, group) => {
                    const cachedGroup = groupData?.find(
                      (g) => g.id === group.id
                    )
                    if (cachedGroup) {
                      groups.push({
                        type: 'user_group',
                        id: group.id,
                        meta: {
                          title: cachedGroup.attributes.name,
                          count: cachedGroup.relationships.members.data.length,
                        },
                      })
                    }

                    return groups
                  },
                  []
                ) ?? []

              draftDocument.meta.access.resources.forEach((resource) => {
                const inheritsMembership = access.resources?.find(
                  (r) => r.id === resource.id
                )?.meta.inherits_membership

                if (inheritsMembership) {
                  resource.meta.inherits_membership = inheritsMembership
                }
              })
            }

            return draftDocument
          })
      )
    },
    onSuccess(data) {
      // If we have the meta.access structure in the response,
      // use it to update the cache instead of invalidating
      if (data.meta?.access != null) {
        queryClient.setQueryData<OpalApiResponseDocument<'ReadContentV3', 200>>(
          globalQueryKeys.contentAccess(contentUuid),
          (cachedData) =>
            produce(cachedData, (draftDocument) => {
              if (draftDocument?.data) {
                draftDocument.meta = data.meta
              }

              return draftDocument
            })
        )
      } else {
        queryClient.invalidateQueries(
          globalQueryKeys.contentAccess(contentUuid)
        )
      }
    },
  })
}
